import styled from "styled-components";
import {
  QUERIES,
  SPACING,
  FONTS,
  FONT_SIZES,
} from "../../../../../utils/constants";
import { UnavailablePointsNote } from "../../../historyPoint";
import { SECTION_GAP } from "../../constants";
import { LineStyle } from "./types";
import { BaseButton } from "../../../../../components/Button";

export const PerformanceCard = styled.section`
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  box-shadow: var(--shadow-card-rest);
  font-family: ${FONTS.serif};
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${SPACING.md - SPACING.xxs}px;
  padding: ${SPACING.md}px ${SPACING.sm + SPACING.xxs}px
    ${SPACING.md - SPACING.xxs}px;
  margin-top: ${SECTION_GAP}px;

  @media ${QUERIES.tabletAndUp} {
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: ${SPACING.md + SPACING.xs}px;
    padding: ${SPACING.md + SPACING.xs}px ${SPACING.lg - SPACING.xxs}px
      ${SPACING.md - SPACING.xxs}px;
  }
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  padding: 0 ${SPACING.xs + SPACING.xxs}px;

  @media ${QUERIES.tabletAndUp} {
    grid-column: 1;
    grid-row: 1;
    padding: 0;
  }
`;

// Follows the chart in the DOM so mobile focus order matches what's drawn; tablet+ lifts it into the title row.
export const RangeArea = styled.div`
  justify-self: center;

  @media ${QUERIES.tabletAndUp} {
    grid-column: 2;
    grid-row: 1;
    align-self: start;
    justify-self: end;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-family: ${FONTS.serif};
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-default);
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: ${FONT_SIZES.sm};
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const LegendRow = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.sm + SPACING.xxs}px ${SPACING.lg}px;
  padding: 0 ${SPACING.xs + SPACING.xxs}px;

  @media ${QUERIES.tabletAndUp} {
    padding: 0;
  }
`;

export const LegendItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  font-size: ${FONT_SIZES.sm};
  line-height: 1.4;
  color: var(--color-text-secondary);
`;

export const LegendSwatch = styled.span<{ $color: string; $style: LineStyle }>`
  width: 24px;
  height: 0;
  border-radius: 3px;
  border-top: 3px ${({ $style }) => $style} ${({ $color }) => $color};
`;

export const LegendToggle = styled(BaseButton)<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-height: 32px;
  margin-block: -7px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${({ $active }) =>
    $active ? "var(--color-text-default)" : "var(--color-text-secondary)"};
  font: inherit;
  font-size: ${FONT_SIZES.sm};
  line-height: 1.4;
  opacity: ${({ $active }) => ($active ? 1 : 0.62)};
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
    color: var(--color-text-default);
    opacity: 1;
  }

  @media ${QUERIES.tabletAndDown} {
    min-height: 44px;
    margin-block: -13px;
  }
`;

export const ChartNotice = styled.div`
  grid-column: 1 / -1;
`;

export const ChartBody = styled.div`
  grid-column: 1 / -1;
  min-width: 0;
`;

export const ChartNote = styled(UnavailablePointsNote)`
  grid-column: 1 / -1;
  margin-top: 0;
`;

export const TaxGapNote = styled.p`
  grid-column: 1 / -1;
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  line-height: 1.4;
`;

export const EmptyMessage = styled.p`
  margin: 0;
  padding: ${SPACING.xxl}px ${SPACING.md}px;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--text-md);
`;
